/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { ActionButton } from '@fluentui/react/lib/Button';
import { DetailsList, IColumn, IDetailsRowStyleProps, IDetailsRowStyles, SelectionMode } from '@fluentui/react/lib/DetailsList';
import { Separator } from '@fluentui/react/lib/Separator';
import { IStackItemProps, IStackItemSlots, Stack, StackItem } from '@fluentui/react/lib/Stack';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { useSelection } from 'src/core/hooks/useSelection';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { QnaMakerAnswerDetails } from 'src/qnaMaker/appShell/qnaMakerTestPane/QnaMakerAnswerDetails';
import { QnaMakerPredictedAnswer, QnaMakerPredictedQnA } from 'src/qnaMaker/core/types/QnaMakerPredictedQnA';

type QnaMakerDetailedBladeProps = {
    qna: QnaMakerPredictedQnA;
    defaultAnswer: string;
    onEditQnA: (answerId: number) => void;
    onAddQuestion: (answerId: number, questions: string[]) => void;
};

const messages = defineMessages({
    questionLabel: { id: 'qna.qnaMakerDetailedBlade.questionLabel', defaultMessage: 'Question' },
    topAnswersTitle: { id: 'qna.qnaMakerDetailedBlade.topAnswersTitle', defaultMessage: 'Top answers' },
    answerSpanTextLabel: { id: 'qna.qnaMakerDetailedBlade.answerSpanTextLabel', defaultMessage: 'Answer-span text:' },
    topAnswersSubtitle: { id: 'qna.qnaMakerDetailedBlade.topAnswersSubtitle', defaultMessage: 'Select the most appropriate answer:' },
    saveQuery: { id: 'qna.qnaMakerDetailedBlade.saveQuery', defaultMessage: 'Save this query as alternate question for this answer' },
    disabledSaveTooltip: {
        id: 'qna.qnaMakerDetailedBlade.disabledSaveTooltip',
        defaultMessage: 'Cannot add Q&A pairs to unstructured sources'
    },
    disabledEditSaveTooltip: {
        id: 'qna.qnaMakerDetailedBlade.disabledEditSaveTooltip',
        defaultMessage: 'Cannot edit Q&A pairs of unstructured sources'
    }
});

const rowStyles = classNamesFunction<IDetailsRowStyleProps, IDetailsRowStyles>()({ check: { marginTop: '13px !important' } });
const answersListContainerStyles = classNamesFunction<IStackItemProps, IComponentStyles<IStackItemSlots>>()({
    root: { overflowY: 'auto', flex: 1 }
});

export const QnaMakerDetailedBlade = observer((props: QnaMakerDetailedBladeProps) => {
    const { qna, defaultAnswer, onAddQuestion, onEditQnA } = props;
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();
    const { isXLargeOrGreater } = useResponsiveMode();
    const { selection, selectedItems } = useSelection<QnaMakerPredictedAnswer>(SelectionMode.single);
    const isUnstructuredSource = selectedItems?.[0]?.id === 0;

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);
    const { languageDirection } = rootStore.currentQnaMakerProjectStore;

    const isSaveDisabled = !canWrite || !selectedItems.length || isUnstructuredSource;
    const isSaveTooltipHidden = (canWrite && !isUnstructuredSource) || !selectedItems.length;

    const answersColumns: IColumn[] = [
        {
            name: '',
            key: 'answer',
            minWidth: 100,
            onRender: (item: QnaMakerPredictedAnswer) => (
                <QnaMakerAnswerDetails
                    answer={item}
                    onEditQnA={onEditQnA}
                    isEditDisabled={item.id === 0}
                    isDefaultAnswer={item.text === defaultAnswer}
                    editTooltipContent={item.id === 0 && localize(messages.disabledEditSaveTooltip)}
                />
            )
        }
    ];

    const addQuestionToSelectedAnswer = () => {
        const selectedAnswer = selectedItems[0];
        const newQuestionsList = [...selectedAnswer.questions, qna.question].filter(
            (item, index, arr) => arr.findIndex(a => a === item) === index
        );
        onAddQuestion(selectedAnswer.id, newQuestionsList);
    };

    return (
        <Stack verticalFill tokens={{ childrenGap: 14 }}>
            <Stack tokens={{ childrenGap: 6 }}>
                <LuisText variant="mediumPlus" fontWeight={500}>
                    {localize(messages.questionLabel)}
                </LuisText>
                <LuisText dir={languageDirection} styles={{ root: { paddingLeft: '20px' } }}>
                    {qna.question}
                </LuisText>
            </Stack>

            <LuisText variant="mediumPlus" fontWeight={500}>
                {localize(messages.topAnswersTitle)}
            </LuisText>

            <Stack tokens={{ childrenGap: 4 }} styles={{ root: { overflowY: isXLargeOrGreater && 'hidden' } }}>
                <LuisText>{localize(messages.topAnswersSubtitle)}</LuisText>

                <Stack styles={{ root: { overflowY: isXLargeOrGreater && 'hidden' } }}>
                    <RBACTooltip
                        displayedUserRole={displayedUserRole}
                        content={isUnstructuredSource && localize(messages.disabledSaveTooltip)}
                        hidden={isSaveTooltipHidden}
                        data-automation-id="saveQueryButtonLanguageToolTip"
                        componentSurroundedByToolTip={
                            <ActionButton
                                iconProps={{ iconName: 'Save' }}
                                text={localize(messages.saveQuery)}
                                onClick={addQuestionToSelectedAnswer}
                                disabled={isSaveDisabled}
                            />
                        }
                    />

                    <Separator styles={{ root: { height: '1px' } }} />

                    <StackItem styles={answersListContainerStyles}>
                        <DetailsList
                            items={qna.answers}
                            selection={selection}
                            isHeaderVisible={false}
                            columns={answersColumns}
                            selectionMode={SelectionMode.single}
                            onRenderRow={(rowProps, defaultRenderer) =>
                                defaultRenderer({
                                    ...rowProps,
                                    styles: mergeStyleSets(rowStyles, rowProps.styles)
                                })
                            }
                        />
                    </StackItem>
                </Stack>
            </Stack>
        </Stack>
    );
});
